import React, { FC, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import first from 'lodash/first';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import last from 'lodash/last';
import map from 'lodash/map';
import noop from 'lodash/noop';
import orderBy from 'lodash/orderBy';
import size from 'lodash/size';
import sortBy from 'lodash/sortBy';
import styled from 'styled-components';
import { produce } from 'immer';
import { useDispatch, useSelector } from 'react-redux';

import Form, { useForm, FormEditorProps } from 'src/presentation/components/forms/Form';
import HelpButton, { HelpButtonPosition } from './components/HelpButton';
import Ivr from 'src/models/Ivrs/Ivr';
import IvrTree from './components/ivrTree/ivrTree';
import NODE_TYPES from 'src/models/Ivrs/NodeTypes';
import ProgressBar from '@ad-scripts/presentation/components/common/progress_bar.jsx';
import SidePanel from './components/sidePanel/SidePanel';
import TreeData from './components/ivrTree/models/TreeData';
import VersionManager, { VersionManagerContext } from './components/ivrTree/components/versionBar/VersionManager';
import backToListView from 'src/actions/shared/backToListView';
import ivrResource from 'src/actions/ivrs/ivrResource';
import ivrsResource from 'src/actions/ivrs/ivrsResource';
import ivrsTreeDataResource from 'src/actions/ivrs/ivrsTreeDataResource';
import organizationResource, { Organization } from 'src/actions/organization/organizationResource';
import useDocumentResource, { useResourceValue } from 'src/presentation/hooks/useDocumentResource';
import useTransformer from 'src/presentation/components/pages/ivr/components/ivrTree/hooks/useTransformer';
import useTreeNodesPositions from './hooks/useTreeNodesPositions';
import { Body, Section } from 'src/presentation/components/forms/Layout';
import { IVR_TREE_TOP_MARGIN, NODE_LIST_ID, SIDE_PANEL_WIDTH } from './constants/ivrConstants';
import { IvrConfig } from './components/ivrTree/config/ivrConfig';
import { IvrContext } from './IvrConfigurations';
import { IvrNodePosition } from 'src/models/Ivrs/IvrTreeData';
import { PublishDescriptionModal } from './components/PublishDescriptionModal';
import { RootState } from 'src/actions/configuration/rootReducer';
import { STATUS_SUCCEEDED, STATUS_LOADING, STATUS_FAILED, useResourceValues } from 'src/presentation/hooks/useResource';
import { Spinner } from 'src/presentation/components/pages/shared/Spinner';
import { calculateNodesPositions } from 'src/presentation/components/pages/ivr/components/ivrTree/helpers/nodesHelper';
import { getNodeListMap, urlHashToObj, deleteNodeFromTree } from './helpers/ivrHelpers';
import { toastError } from 'src/actions/toast';
import useFetchIvrWithTreeData from './hooks/useFetchIvrWithTreeData';
import NODES_MAPPER from 'src/presentation/components/pages/ivr/constants/nodesMapper';

type SetFormValidFunc = (nodeId: string, isValid: boolean) => void;

type IvrEditorContext = {
  currentComponentId: string;
  invalidFormList: string[];
  ivrConfigurationHasBeenFetched: boolean;
  nodeIdForDeleting: string;
  onNodeSelected: Function;
  setFormIsValid: SetFormValidFunc;
  setInputData: Function;
  setNodeIdForDeleting: Function;
  treeData?: TreeData | null;
  setIsSaveInProgress: Function;
  organization?: Organization;
};

export const IvrEditorContext = createContext<IvrEditorContext>({
  currentComponentId: '',
  invalidFormList: [],
  ivrConfigurationHasBeenFetched: false,
  onNodeSelected: noop,
  nodeIdForDeleting: '',
  setFormIsValid: noop,
  setInputData: noop,
  setIsSaveInProgress: noop,
  setNodeIdForDeleting: noop,
});

type SidePanelContextProps = {
  currentNodeNavigationHistoryIndex: number;
  nodeNavigationHistory: string[];
  onNodeSelected: Function;
  setCurrentNodeNavigationHistoryIndex: Function;
};

export const SidePanelContext = createContext<SidePanelContextProps>({
  currentNodeNavigationHistoryIndex: 0,
  nodeNavigationHistory: [],
  onNodeSelected: noop,
  setCurrentNodeNavigationHistoryIndex: noop,
});

export type IvrEditorLocationStateContext = {
  locationState?: IvrNodePosition;
  setLocationState: Function;
  updateLocations: Function;
};

export const IvrEditorLocationStateContext = createContext<IvrEditorLocationStateContext>({
  setLocationState: noop,
  updateLocations: noop,
});

type SelectorsFromHash = {
  version: number;
  nodeId: string;
};

type ActionFn = {
  (config: Ivr, nodeId: string): void;
};

const useIvrConfiguration = ({ id, ivrs, setIsSaveInProgress }) => {
  const dispatch = useDispatch();
  const [invalidFormList, setInvalidFormList] = useState<string[]>([]);
  const [ivrConfigInitial, setIvrConfigInitial] = useState<any>({});
  const [data, setData] = useState();
  const [currentVersion, setCurrentVersion] = useState(0);
  const [isNew, setIsNew] = useState(false);
  const { locationState, setLocationState, updateLocations } = useTreeNodesPositions({
    id,
    version: currentVersion,
  });

  const ivrSelectorsFromHash: SelectorsFromHash = {
    version: Number(get(urlHashToObj(), 'version', 0)),
    nodeId: get(urlHashToObj(), 'nodeId', ''),
  };

  const versionFromUrlHash = ivrSelectorsFromHash.version;
  const latestVersion = get(ivrs, '[0].version', '');
  const archivedAt = get(ivrs, '[0].archivedAt', '');
  const version: number = versionFromUrlHash ? Number(versionFromUrlHash) : latestVersion;

  const nodeIdFromUrlHash: string = ivrSelectorsFromHash.nodeId;

  // next line is calculating if we need fetch/re-fetch data
  // case: to prevent of re-fetching data after user modify node or nodes
  // currentVersion variable is for storing previous version
  // version variable is incoming version from request - will the the same as currentVersion or
  // if it comes from server will be currentVersion+1
  // and in this situation we don't need to make another call for the data
  // also POST request receive all data that we need and we do not need to make another GET call
  const fetchData: boolean = (currentVersion !== version - 1 && currentVersion !== version) || id !== get(data, 'id');
  const [ivrConfigurationHasBeenFetched, setIvrConfigurationHasBeenFetched] = useState(fetchData);

  useEffect(() => {
    setIvrConfigurationHasBeenFetched(fetchData);
  }, [fetchData, setIvrConfigurationHasBeenFetched]);

  const {
    loadingState,
    ivrsTreeData,
    ivrConfig,
    errors: ivrErrors,
  } = useFetchIvrWithTreeData({
    fetchData,
    id,
    version,
    setIsSaveInProgress,
  });

  const { errors: ivrToolsErrors } = useResourceValues(ivrsResource);

  const errors = useMemo(() => ivrToolsErrors.concat(ivrErrors), [ivrErrors, ivrToolsErrors]);

  useEffect(() => {
    if (loadingState === STATUS_SUCCEEDED) {
      const initialNodeList = get(ivrConfig, 'nodes', []);

      const nodesWithName = map(initialNodeList, node => {
        const name = isNil(get(node, 'ivrNode.name')) ? get(node, 'ivrNode.description') : get(node, 'ivrNode.name');
        const ivrNode = { ...get(node, 'ivrNode'), name };
        return { ...node, ivrNode };
      });

      const sortedNodes = sortBy(nodesWithName, node => get(node, 'ivrNode.name'));
      const mappedSortedNodes = getNodeListMap(sortedNodes);

      const nodesAreChanged =
        get(data, 'rootNodeId') !== get(ivrConfig, 'rootNodeId') ||
        !isEqual(get(data, 'nodesMap', {}), mappedSortedNodes);

      setIsNew(version === 1 && isEmpty(mappedSortedNodes));

      if (!data || nodesAreChanged) {
        const config: any = produce<any>(ivrConfig, draft => {
          draft.nodesMap = mappedSortedNodes;

          delete draft.nodes;
        });

        setIvrConfigInitial(config);
        setData(config);
        if (isEmpty(ivrsTreeData)) {
          const [nodesLocations]: any = calculateNodesPositions({ ivrConfig: new IvrConfig(config) });
          dispatch(ivrsTreeDataResource.actions.fetchSuccess(nodesLocations));
          setLocationState(nodesLocations);
        } else {
          setLocationState(ivrsTreeData);
        }

        dispatch(ivrResource.actions.resetErrors({}));
        dispatch(ivrResource.effects.validateConfiguration(config));
        setIvrConfigurationHasBeenFetched(true);
      }
      setCurrentVersion(version);
    }
  }, [
    data,
    dispatch,
    ivrConfig,
    ivrsTreeData,
    loadingState,
    setCurrentVersion,
    setIsSaveInProgress,
    setIvrConfigurationHasBeenFetched,
    setLocationState,
    version,
  ]);

  const invalidNodesIds = useMemo(
    () =>
      errors.length
        ? filter(
            map(errors, ({ nodeId }) => nodeId),
            nodeId => !!nodeId
          )
        : [],
    [errors]
  );

  useEffect(() => {
    // The error list may become empty during the fetching,
    // so invalidNodesIds should be updated only when the fetch is completed.
    if (loadingState === STATUS_SUCCEEDED) {
      setInvalidFormList(invalidNodesIds);
    }
  }, [invalidNodesIds, loadingState]);

  const initIvr = useMemo(() => ({ ...ivrConfigInitial, version }), [ivrConfigInitial, version]);
  const isEditingAllowed = version === latestVersion && !archivedAt;
  const state = !isEmpty(data) && id === get(data, 'id') ? STATUS_SUCCEEDED : STATUS_LOADING;

  return {
    errors,
    invalidFormList,
    isEditingAllowed,
    isNew,
    ivrConfig: data,
    ivrConfigInitial: initIvr,
    ivrConfigurationHasBeenFetched,
    loadingState: state,
    locationState,
    nodeIdFromUrlHash,
    setCurrentVersion,
    setInvalidFormList,
    setIvrConfigInitial,
    setLocationState,
    updateLocations,
    version,
  };
};

const IvrEditor: FC<FormEditorProps> = ({ item = {}, root }) => {
  const dispatch = useDispatch();
  const orgId = useSelector((state: RootState) => state.auth.orgId);
  const { value: organization } = useDocumentResource(organizationResource, {
    fetch: true,
    params: { orgId },
  });
  const { ivrConfigurations, history } = useContext(IvrContext);
  const [isSaveInProgress, setIsSaveInProgress] = useState(true);
  const [nodeIdForDeleting, setNodeIdForDeleting] = useState('');

  // nodeNavigationHistory and currentNodeNavigationHistoryIndex are used for the back/forward node navigation via arrows in the SidePanel
  const [nodeNavigationHistory, setNodeNavigationHistory] = useState<string[]>([]);
  const [currentNodeNavigationHistoryIndex, setCurrentNodeNavigationHistoryIndex] = useState<number>(0);

  const id = get(item, 'id', '');

  const ivrs = useMemo(
    () =>
      orderBy(
        ivrConfigurations.filter(ivr => ivr.id === id),
        ['version'],
        ['desc']
      ),
    [id, ivrConfigurations]
  );

  const {
    errors: ivrErrors,
    invalidFormList,
    isEditingAllowed,
    isNew,
    ivrConfig,
    ivrConfigInitial,
    ivrConfigurationHasBeenFetched,
    loadingState,
    locationState,
    nodeIdFromUrlHash,
    setCurrentVersion,
    setInvalidFormList,
    setLocationState,
    updateLocations,
    version,
  } = useIvrConfiguration({ id, ivrs, setIsSaveInProgress });
  const { treeData, ivrFrameSize, setInputData } = useTransformer(ivrConfig!, locationState);
  const { persistingState } = useResourceValues(ivrsResource);

  useEffect(() => {
    if (persistingState === STATUS_FAILED) {
      setIsSaveInProgress(false);
    }
  }, [persistingState, setIsSaveInProgress]);

  const handleError = useCallback(() => {
    dispatch(toastError('Something went wrong.'));
    backToListView();
  }, [dispatch]);

  if (!id || !version) {
    handleError();
  }

  useEffect(() => {
    if (loadingState === STATUS_FAILED) {
      handleError();
    }
  }, [loadingState, handleError]);

  const description = get(ivrConfig, 'description', '');

  const [currentComponentId, setCurrentComponentId] = useState(nodeIdFromUrlHash);
  const [currentComponentOption, setCurrentComponentOption] = useState<any>(null);

  const addToHistory = useCallback(
    nodeId => {
      if (nodeId === NODE_LIST_ID) {
        // resets the history pointer
        setCurrentNodeNavigationHistoryIndex(nodeNavigationHistory.length);
        return;
      }

      if (nodeId && last(nodeNavigationHistory) !== nodeId) {
        // updates the history and sets currentNodeNavigationHistoryIndex to the last history item
        setCurrentNodeNavigationHistoryIndex(nodeNavigationHistory.length);
        setNodeNavigationHistory([...nodeNavigationHistory, nodeId]);
      }
    },
    [nodeNavigationHistory, setCurrentNodeNavigationHistoryIndex, setNodeNavigationHistory]
  );

  const { selectedNodeId } = useResourceValue(ivrResource);

  useEffect(() => {
    if (selectedNodeId) {
      setCurrentComponentId(selectedNodeId);
      setCurrentComponentOption(null);
    }
  }, [setCurrentComponentOption, setCurrentComponentId, selectedNodeId]);

  const onNodeSelected = useCallback(
    (nodeId, { doNotUpdateHistory } = {}) => {
      if (nodeId !== currentComponentId) {
        setCurrentComponentId(nodeId);
        setCurrentComponentOption(null);

        const updateHistory = !doNotUpdateHistory;
        updateHistory && addToHistory(nodeId);
      }
    },
    [addToHistory, currentComponentId, setCurrentComponentId, setCurrentComponentOption]
  );

  const onOptionNodeSelected = node => {
    if (node.parentNodeId) {
      onNodeSelected(node.parentNodeId);
      setCurrentComponentOption(node);
    }
  };

  const renderedIvrTree = treeData && (
    <StyledBody data-aid="ivr-editor" header={id} description={description} isNew={isNew}>
      <TreeWrapper>
        <IvrTree
          filter={currentComponentOption || currentComponentId}
          hideTree={isNew}
          ivrFrameSize={ivrFrameSize}
          nodeSelected={onNodeSelected}
          optionNodeSelected={onOptionNodeSelected}
          treeData={treeData}
          readOnly={!isEditingAllowed}
        />
      </TreeWrapper>
      )
    </StyledBody>
  );

  const sidePanelContext = {
    currentNodeNavigationHistoryIndex,
    nodeNavigationHistory,
    onNodeSelected,
    setCurrentNodeNavigationHistoryIndex,
  };

  const renderedSidePanel = isNew ? null : (
    <SidePanelContext.Provider value={sidePanelContext}>
      <SidePanel
        currentComponentId={currentComponentId}
        currentComponentOption={currentComponentOption}
        isEditingAllowed={isEditingAllowed}
        isSaveInProgress={isSaveInProgress}
        ivrConfig={ivrConfig}
        ivrErrors={ivrErrors}
        setCurrentComponentOption={setCurrentComponentOption}
        setNodeIdForDeleting={setNodeIdForDeleting}
        treeData={treeData}
      />
    </SidePanelContext.Provider>
  );

  const setFormIsValid = useCallback(
    (nodeId: string, isValid: boolean) => {
      let clonedFormList = [...invalidFormList];
      if (!invalidFormList.includes(nodeId) && !isValid) {
        clonedFormList.push(nodeId);
      }
      if (invalidFormList.includes(nodeId) && isValid) {
        clonedFormList = clonedFormList.filter(item => item !== nodeId);
      }
      setInvalidFormList(clonedFormList);
    },
    [invalidFormList, setInvalidFormList]
  );

  const editorContextValue = useMemo(
    () => ({
      currentComponentId,
      invalidFormList,
      ivrConfigurationHasBeenFetched,
      nodeIdForDeleting,
      onNodeSelected,
      organization,
      setFormIsValid,
      setInputData,
      setIsSaveInProgress,
      setNodeIdForDeleting,
      treeData,
    }),
    [
      currentComponentId,
      invalidFormList,
      ivrConfigurationHasBeenFetched,
      nodeIdForDeleting,
      onNodeSelected,
      organization,
      setFormIsValid,
      setInputData,
      setIsSaveInProgress,
      setNodeIdForDeleting,
      treeData,
    ]
  );

  const locationStateContextValue = { locationState, organization, setLocationState, updateLocations };

  const renderedPage =
    loadingState !== 'succeeded' ? (
      <Spinner data-aid="ivr-data-spinner" />
    ) : (
      <IvrEditorContext.Provider value={editorContextValue}>
        <IvrEditorLocationStateContext.Provider value={locationStateContextValue}>
          <IvrEditorForm
            currentComponentId={currentComponentId}
            errors={ivrErrors}
            isNew={isNew}
            isSaveInProgress={isSaveInProgress}
            ivrConfig={ivrConfig}
            ivrConfigInitial={ivrConfigInitial}
            ivrTree={renderedIvrTree}
            ivrs={ivrs}
            root={root}
            selectedVersion={version}
            setCurrentVersion={setCurrentVersion}
            setIsSaveInProgress={setIsSaveInProgress}
            setNodeNavigationHistory={setNodeNavigationHistory}
            sidePanel={renderedSidePanel}
            history={history}
          />
          <HelpButton position={HelpButtonPosition.BOTTOM_LEFT} />
        </IvrEditorLocationStateContext.Provider>
      </IvrEditorContext.Provider>
    );

  return renderedPage;
};

const IvrEditorForm = ({
  currentComponentId,
  errors,
  history,
  isNew,
  isSaveInProgress,
  ivrConfig,
  ivrConfigInitial,
  ivrs,
  ivrTree,
  root,
  selectedVersion,
  setCurrentVersion,
  setIsSaveInProgress,
  setNodeNavigationHistory,
  sidePanel,
}) => {
  const [showPublishDescriptionModal, setShowPublishDescriptionModal] = useState(false);
  const dispatch = useDispatch();
  const { createNewVersion, restoreVersion, update } = ivrsResource.effects;
  const { resetErrors } = ivrResource.actions;

  const { nodeIdForDeleting, setInputData, setNodeIdForDeleting, ivrConfigurationHasBeenFetched } =
    useContext(IvrEditorContext);

  const saveHandler = useOnSave({
    ivrConfigInitial,
    currentNodeId: currentComponentId,
    version: selectedVersion,
    setIsSaveInProgress,
  });

  const blurHandler = useOnBlur({
    currentNodeId: currentComponentId,
    ivrConfigInitial,
    setInputData,
    setIsSaveInProgress,
    version: selectedVersion,
  });

  const onDeleteHandler = useOnDelete({
    ivrConfigInitial,
    setInputData,
    setIsSaveInProgress,
    version: selectedVersion,
  });

  const formContext = useForm({
    add: createNewVersion,
    errors,
    item: ivrConfig,
    onBlur: blurHandler,
    onDelete: onDeleteHandler,
    onSave: saveHandler,
    resetErrors,
    root,
    update,
    validate: ivrResource.effects.validateConfiguration,
  });

  const { overrideState, onDelete } = formContext;

  useEffect(() => {
    if (ivrConfigurationHasBeenFetched) {
      overrideState(ivrConfig);
    }
  }, [ivrConfig, ivrConfigurationHasBeenFetched, overrideState]);

  useEffect(() => {
    if (nodeIdForDeleting) {
      // @ts-ignore
      onDelete(nodeIdForDeleting, selectedVersion);
      setNodeIdForDeleting('');
    }
  }, [nodeIdForDeleting, onDelete, selectedVersion, setNodeIdForDeleting]);

  const onBackToLatest = () => {
    setIsSaveInProgress(true);
    setCurrentVersion(-1);
    history.push('#');
    setNodeNavigationHistory([]);
  };

  const onVersionChanged = (version: number) => {
    setIsSaveInProgress(true);
    setCurrentVersion(-1);
    history.push(`#version=${version}`);
    setNodeNavigationHistory([]);
  };

  const onPublish = useCallback(() => {
    setShowPublishDescriptionModal(true);
  }, []);

  const restore = useCallback(async () => {
    setIsSaveInProgress(true);
    await dispatch(restoreVersion({ id: ivrConfig.id, version: ivrConfig.version }));
    history.push('#');
  }, [dispatch, history, ivrConfig, restoreVersion, setIsSaveInProgress]);

  const versionManagerContext = {
    backToLatest: onBackToLatest,
    changeVersion: onVersionChanged,
    isNew,
    isSaveInProgress,
    items: ivrs,
    publishIvr: onPublish,
    restoreIvr: restore,
    selected: selectedVersion,
  };

  const versionManager = (
    <StyledHeader>
      <ViewButtonWrapper isNew={isNew} data-aid={isSaveInProgress ? 'version-manager-saving' : 'version-manager-saved'}>
        <VersionManagerContext.Provider value={versionManagerContext}>
          <VersionManager data-aid="version-manager" errors={errors} />
        </VersionManagerContext.Provider>
      </ViewButtonWrapper>
    </StyledHeader>
  );

  const publishDescriptionModal = showPublishDescriptionModal && (
    <PublishDescriptionModal
      errors={errors}
      item={ivrConfig}
      onCancel={() => setShowPublishDescriptionModal(false)}
      setIsSaveInProgress={setIsSaveInProgress}
    />
  );
  const progressBar = <ProgressBar data-aid="progress-bar" isVisible={isSaveInProgress} />;

  return (
    <Form context={formContext} preventSaveOnEnter>
      {progressBar}
      {versionManager}
      {ivrTree}
      {sidePanel}
      {publishDescriptionModal}
    </Form>
  );
};

// Clones the provided config and adds some additional details such as version and change comment
// After that it calls actions[] one by one, if they are provided
const adjustNodeBeforeSave = ({
  actions = [] as ActionFn[], // ActionFn(config, nodeId) should mutate the config
  nodeId = '',
  version,
  versionCommentPrefix = '',
  workingState,
  rootNodeIdHasChanged = false,
}): Ivr => {
  let versionComment: string;

  if (rootNodeIdHasChanged) {
    versionComment = 'Root Node has changed';
  } else {
    const updatedItem = workingState.nodesMap[nodeId];
    const commentContent = get(updatedItem, 'ivrNode.name');
    const commentLabel = get(NODES_MAPPER, `[${updatedItem.type}].treeNodeLabel`, '');
    versionComment = `${versionCommentPrefix}${commentLabel}: ${commentContent}`;
  }

  const stateCopy = cloneDeep(workingState);

  const adjustedConfig = {
    ...stateCopy,
    comment: versionComment,
    version,
    nodes: map(stateCopy.nodesMap, node => {
      if ([NODE_TYPES.IVR_NODE_DIAL, NODE_TYPES.IVR_NODE_VOICEMAIL].includes(node.type)) {
        node.ivrNode.name = node.ivrNode.description || node.ivrNode.selection || node.ivrNode.id;
      }
      return node;
    }),
  };
  delete adjustedConfig.nodesMap;

  if (!isEmpty(actions)) {
    actions.forEach(action => action(adjustedConfig, nodeId));
  }

  return adjustedConfig;
};

const useOnDelete = ({ ivrConfigInitial, setInputData, setIsSaveInProgress, version }) => {
  const dispatch = useDispatch();
  const { onNodeSelected, treeData } = useContext(IvrEditorContext);

  const deleteHandler = props => {
    const { add, itemId: nodeId, onSave, overrideState, workingState } = props;

    dispatch(ivrResource.effects.validateConfiguration(workingState));

    const adjustedConfig = adjustNodeBeforeSave({
      actions: [deleteNodeFromTree],
      nodeId,
      version,
      versionCommentPrefix: 'Delete ',
      workingState,
    });

    setInputData(adjustedConfig);
    overrideState && overrideState(adjustedConfig);
    setIsSaveInProgress(true);
    onSave({ ivrConfigInitial, workingState: adjustedConfig, add });

    const parents = treeData ? treeData.getParents(nodeId) : undefined;
    !isEmpty(parents) && onNodeSelected && onNodeSelected(first(parents));

    isEmpty(parents) && onNodeSelected(NODE_LIST_ID);
  };

  return useCallback(deleteHandler, [
    dispatch,
    ivrConfigInitial,
    onNodeSelected,
    setInputData,
    setIsSaveInProgress,
    treeData,
    version,
  ]);
};

function useOnSave({ ivrConfigInitial, currentNodeId, version, setIsSaveInProgress }) {
  const dispatch = useDispatch();

  const saveHandler = ({ add, workingState }) => {
    dispatch(
      add(
        { version, id: workingState.id },
        { ...workingState, version },
        {
          customToastMsg: null,
          disableWindowBack: true,
        }
      )
    );
  };

  return useCallback(saveHandler, [
    saveHandler,
    currentNodeId,
    dispatch,
    ivrConfigInitial,
    setIsSaveInProgress,
    version,
  ]);
}

function useOnBlur({ currentNodeId, ivrConfigInitial, setInputData, setIsSaveInProgress, version }) {
  const dispatch = useDispatch();

  const handler = props => {
    const { add, currentState, onSave, workingState } = props;

    const state = currentState || workingState;
    const initNodeValue = get(ivrConfigInitial.nodesMap, currentNodeId);
    const workingNode = get(state.nodesMap, currentNodeId);

    const nodeIsChanged = !isEqual(get(initNodeValue, 'ivrNode'), get(workingNode, 'ivrNode'));
    const nodeListIsChanged = size(state.nodesMap) !== size(ivrConfigInitial.nodesMap);
    const rootNodeIdHasChanged = get(state, 'rootNodeId') !== get(ivrConfigInitial, 'rootNodeId');

    dispatch(ivrResource.effects.validateConfiguration(state));

    const configurationIsDifferent = nodeIsChanged || nodeListIsChanged || rootNodeIdHasChanged;

    if (configurationIsDifferent) {
      setIsSaveInProgress(true);
      const adjustedConfig = adjustNodeBeforeSave({
        nodeId: currentNodeId,
        version,
        versionCommentPrefix: '',
        workingState: state,
        rootNodeIdHasChanged,
      });

      setInputData(adjustedConfig);

      onSave({ ivrConfigInitial, workingState: adjustedConfig, add });
    }
  };

  return useCallback(handler, [currentNodeId, dispatch, ivrConfigInitial, setInputData, setIsSaveInProgress, version]);
}

const ViewButtonWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  margin-right: auto;
  max-width: 100%;
  padding: 0;
  width: ${p => (p.isNew ? '100%' : `calc(100% - ${SIDE_PANEL_WIDTH})`)};
  & > button {
    margin: 0 8px;
  }
`;

const StyledBody = styled(Body)`
  padding: 0;
  max-width: 100%;
  width: ${p => (p.isNew ? '100%' : `calc(100% - ${SIDE_PANEL_WIDTH})`)};
  height: calc(100vh - ${IVR_TREE_TOP_MARGIN});
  margin-right: auto;
`;

const TreeWrapper = styled(Section)`
  width: 100%;
  height: 100%;
`;

const StyledHeader = styled.div`
  justify-content: center;
  margin: 15px 0 0;
  width: 100%;
`;

export default IvrEditor;
